import {
	IAvailableDates,
	IAvailableTimestamps,
	IId,
	IIsActive,
	TIdSystemId,
	TNameTitle,
	TNameTitleColor,
} from '@interfaces/common/common';
import { IItemsResponse } from '@interfaces/common/response';
import {
	IChangeMonitoringDTO,
	IChangeMonitoringEditDTO,
	IChangeMonitoringStatusesHistoryDTO,
	IGroupNumber,
	INetworkElementConfigurationCandidateDTO,
	INetworkElementConfigurationsByDateDTO,
	INetworkElementConfigurationsDTO,
	INetworkElementConfigurationsExportDTO,
	INetworkElementConfigurationsFilterAction,
	INetworkElementNoteDTO,
	IValidationRuleDTO,
	IValidationRuleFolderDTO,
	IWithNetworkElementId,
	IWithUpdateId,
} from '@interfaces/networkElementConfigurations/dto/networkElementConfigurationsDTO';
import {
	IChangeMonitoring,
	IChangeMonitoringCard,
	IChangeMonitoringStatusesHistory,
	IChangeMonitoringUser,
	INetworkElementConfiguration,
	INetworkElementConfigurationIdentifier,
	INetworkElementConfigurationView,
} from '@interfaces/networkElementConfigurations/networkElementConfigurations';
import { ICandidateNetworkElementDTO } from '@interfaces/networkElements/dto/networkElements';
import { INote } from '@interfaces/networkElements/networkElements';
import { IFlatTreeValidationRule } from '@components/common/FlatTree/interface';

import { client } from './client';
import { URL } from './urls';

const {
	networkElementsConfigurationsStatuses,
	networkElementsConfigurationsMiddlewares,
	networkElementsConfigurationsTypes,
	networkElementsConfigurations,
	networkElementsConfigurationsByDate,
	networkElementsConfigurationsLatestConfig,
	networkElementsConfigurationsDates,
	networkElementsConfigurationsTimestamps,
	networkElementsConfigurationsCandidate,
	netWorkElementsValidationRulesFolders,
	netWorkElementsValidationRules,
} = URL;

export const getNetworkElementConfigurations = (
	dto: INetworkElementConfigurationsDTO,
): Promise<IItemsResponse<INetworkElementConfiguration>> => {
	return client.get(`/${networkElementsConfigurations}`, { params: dto });
};

export const getNetworkElementConfigurationByDate = (
	dto: INetworkElementConfigurationsByDateDTO<number>,
): Promise<INetworkElementConfigurationView> => {
	return client.get(`/${networkElementsConfigurationsByDate}`, { params: dto });
};

export const getNetworkElementConfigurationLatest = (
	dto: INetworkElementConfigurationIdentifier,
): Promise<INetworkElementConfigurationView> => {
	return client.get(`/${networkElementsConfigurationsLatestConfig}`, {
		params: dto,
	});
};

export const getNetworkElementConfigurationDates = (
	dto: INetworkElementConfigurationIdentifier,
): Promise<IAvailableDates> => {
	return client.get(`/${networkElementsConfigurationsDates}`, { params: dto });
};

export const getNetworkElementConfigurationTimestamps = (
	dto: INetworkElementConfigurationsByDateDTO,
): Promise<IAvailableTimestamps[]> => {
	return client.get(`/${networkElementsConfigurationsTimestamps}`, { params: dto });
};

export const getNetworkElementConfigurationsStatuses = (): Promise<TNameTitleColor[]> => {
	return client.get(`/${networkElementsConfigurationsStatuses}`);
};

export const getNetworkElementConfigurationsMiddlewares = (): Promise<TIdSystemId[]> => {
	return client.get(`/${networkElementsConfigurationsMiddlewares}`);
};

export const getNetworkElementConfigurationsTypes = (): Promise<TNameTitle[]> => {
	return client.get(`/${networkElementsConfigurationsTypes}`);
};

export const candidateNetworkElementConfiguration = ({
	updateString,
	networkElementId,
	configId,
	validationRulesFolderId,
	note,
}: INetworkElementConfigurationCandidateDTO): Promise<PromiseSettledResult<ICandidateNetworkElementDTO>[]> => {
	return client.post(`/${networkElementsConfigurationsCandidate}`, {
		updates: updateString.map((updateString) => ({ updateString })),
		networkElementId: networkElementId,
		configId: configId,
		validationRulesFolderId,
		note,
	});
};

export const exportNetworkElementConfigurations = (
	dto: INetworkElementConfigurationsExportDTO,
): Promise<TNameTitle[]> => {
	return client.post(`/${networkElementsConfigurations}/export`, dto);
};

export const exportNetworkElementConfiguration = ({ id }: IId): Promise<TNameTitle[]> => {
	return client.post(`/${networkElementsConfigurations}/${id}/export`, {});
};

export const getChangeMonitoringCard = (dto: IWithNetworkElementId): Promise<IChangeMonitoringCard> => {
	return client.get(`/${networkElementsConfigurations}/edit-config/changes-monitoring/card`, { params: dto });
};

export const getChangeMonitoringActive = (
	dto: IChangeMonitoringDTO,
): Promise<IItemsResponse<IChangeMonitoring>> => {
	return client.get(`/${networkElementsConfigurations}/edit-config/changes-monitoring/active`, { params: dto });
};

export const getChangeMonitoringHistory = (
	dto: IChangeMonitoringDTO,
): Promise<IItemsResponse<IChangeMonitoring>> => {
	return client.get(`/${networkElementsConfigurations}/edit-config/changes-monitoring/history`, { params: dto });
};

export const getChangeMonitoringStatusesHistory = (
	dto: IChangeMonitoringStatusesHistoryDTO,
): Promise<IItemsResponse<IChangeMonitoringStatusesHistory>> => {
	return client.get(`/${networkElementsConfigurations}/edit-config/changes-monitoring/get-history-of-status`, {
		params: dto,
	});
};

export const cancelAllAcceptedNetworkElements = (
	dto: INetworkElementConfigurationsFilterAction,
): Promise<void> => {
	return client.post(`/${networkElementsConfigurations}/edit-config/cancel-all-failed`, dto);
};

export const commitChangeMonitoring = (dto: IWithUpdateId): Promise<void> => {
	return client.post(`/${networkElementsConfigurations}/edit-config/changes-monitoring/commit`, dto);
};

export const cancelChangeMonitoring = (
	dto: IWithUpdateId,
): Promise<void> => {
	return client.post(`/${networkElementsConfigurations}/edit-config/changes-monitoring/cancel`, dto);
};

export const commitAllAcceptedChangeMonitoring = (
	dto: INetworkElementConfigurationsFilterAction,
): Promise<void> => {
	return client.post(`/${networkElementsConfigurations}/edit-config/changes-monitoring/commit-all-accepted`, dto);
};

export const editChangeMonitoring = (
	dto: IChangeMonitoringEditDTO,
): Promise<void> => {
	return client.post(`/${networkElementsConfigurations}/edit-config/changes-monitoring/edit`, dto);
};

export const getChangeMonitoringErrorMessage = (
	dto: IWithUpdateId,
): Promise<string> => {
	return client.get(`/${networkElementsConfigurations}/edit-config/changes-monitoring/get-error-message`, {
		params: dto,
	});
};

export const tryAgainChangeMonitoring = (
	dto: IWithUpdateId,
): Promise<void> => {
	return client.post(`/${networkElementsConfigurations}/edit-config/changes-monitoring/try-again`, dto);
};

export const getChangeMonitoringStatuses = (
	dto: IWithNetworkElementId & IIsActive,
): Promise<TNameTitle[]> => {
	return client.get(`/${networkElementsConfigurations}/edit-config/changes-monitoring/static/statuses`, {
		params: dto,
	});
};

export const getChangeMonitoringUsers = (
	dto: IWithNetworkElementId & IIsActive,
): Promise<IChangeMonitoringUser[]> => {
	return client.get(`/${networkElementsConfigurations}/edit-config/changes-monitoring/static/users`, { params: dto });
};

export const getChangeMonitoringGroups = (
	dto: IWithNetworkElementId,
): Promise<IGroupNumber[]> => {
	return client.get(`/${networkElementsConfigurations}/edit-config/changes-monitoring/static/group-numbers`, {
		params: dto,
	});
};

export const getChangesMonitorUpdateString = (updateId: string): Promise<IValidationRuleDTO> => {
	return client.get(`/${networkElementsConfigurations}/edit-config/changes-monitoring/get-update-string?updateId=${
		updateId}`);
};

export const getNetworkElementsValidationRulesFolders = (): Promise<IValidationRuleFolderDTO[]> => {
	return client.get(netWorkElementsValidationRulesFolders);
};

export const getNetworkElementsValidationRules = (dto: IValidationRuleDTO): Promise<IFlatTreeValidationRule[]> => {
	return client.post(netWorkElementsValidationRules, dto);
};

export const getChangeMonitoringNotes = (
	dto: INetworkElementNoteDTO,
): Promise<INote> => {
	return client.get(`/${networkElementsConfigurations}/edit-config/changes-monitoring/get-update-note`, {
		params: dto,
	});
};
